'use client'

import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react"
import AuthProvider from "./authContext"
import api from "@/util/api"
import customAlert from "@/util/customAlert"
import { useRouter } from "next/navigation"
import ParsedHtmlContent from "@/components/etc/renderContent"
import { useTranslations } from "next-intl"
import { langContext } from "./LangContext"
import { DefaultLangCode, langDefaultList, langTypes, sitelangs } from "@/type/lang"
import { getCookie } from "@/util/cookie"
import { useCookie } from "@/hooks/useCookie"

interface SiteContextType {
    objects: {
        type: number
        content: string
        is_active: boolean
    }[],
    maintenance: {
        is_active: boolean
        is_local: boolean
        content: string
    }[],
    banners: {
        id: number
        redirect_url: string
        is_active: boolean
        name: string
    }[],
    active_languages: langTypes[],
    now_active_language: string
}

export const SettingContext = createContext<{
    setting: SiteContextType | undefined
    SetSetting: (settingData: SiteContextType) => void
}>({
    setting: undefined,
    SetSetting: () => { },

})


export default function SiteProvider({ children }: { children: ReactNode }) {
    const { Setlang, setLangList, frontLang } = useContext(langContext)
    const router = useRouter()
    const [setting, setSetting] = useState<SiteContextType | undefined>()
    const [loading, setLoading] = useState<boolean>(true)

    const t = useTranslations("Text")

    const is_maintenance: boolean = useMemo(() => {
        if (!setting) return false;
        if (!Array.isArray(setting.maintenance)) return false;

        return setting.maintenance[0].is_active ?? false
    }, [setting])

    const is_local: boolean = useMemo(() => {
        if (!setting) return false;
        if (!Array.isArray(setting.maintenance)) return false;

        return setting.maintenance[0].is_local ?? false
    }, [setting])

    function fetchData() {
        api(`/api/site/`)
            .then((res) => res.json())
            .then(({ data, status_code }) => {
                if (status_code == 200) {
                    // console.log(data.objects);
                    SetSetting({
                        objects: data.objects,
                        maintenance: data.maintenance,
                        banners: data.banners,
                        active_languages: data.active_languages,
                        now_active_language: data.now_active_language,
                    })
                }

            })
            .catch((error: Response) => errorResponse(error))
    }
    async function errorResponse(error: Response) {
        const err = await error.json()
        if (error.status == 401) {
            customAlert(err.message, 'warning', () => {
                router.push('/')
                clearSetting();
            })
        }
    }

    function clearSetting() {
        setSetting(undefined)
    }
    function SetSetting(settingData: SiteContextType) {
        setSetting(settingData)
    }
    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        if (!setting) return;

        let siteLang = setting.now_active_language ?? DefaultLangCode
        let siteLangList = setting.active_languages ?? langDefaultList
        if(frontLang){
            siteLang = DefaultLangCode
            siteLangList = langDefaultList
        }

        setLangList(siteLangList)

        const cookieLang: string = getCookie("lang")
        if (!cookieLang) {
            // 첫진입 시, 쿠키에 저장된 lang 값이 없을 경우 사이트 언어 사용.
            Setlang(siteLang)
        } else {
            // 이미 설정된 쿠키가 있다면 쿠키값 적용.
            const langCodeList: string[] = Array.from(new Map(siteLangList.map((el, idx) => [idx, el.code])).values());
            if (langCodeList.includes(cookieLang)) {
                Setlang(cookieLang)
            } else {
                Setlang(langCodeList[0])
            }
        }

    }, [setting])

    const getCookieLang = useCookie("lang")
    useEffect(() => {
        if(!getCookieLang) return;
        if(!setting) return;
        
        const siteLangList = setting.active_languages
        const langCodeList: string[] = Array.from(new Map(siteLangList.map((el, idx) => [idx, el.code])).values());
        if (langCodeList.includes(getCookieLang)) {
            setLoading(false)
        }
        if(frontLang){
            setLoading(false)
        }
    }, [getCookieLang, setting])

    function loadingProcess() {
        if (loading) return `${t("로딩중")}...`; // 로딩중..
        if (!setting) return `${t("로딩중")}...`; // setting 값 받아오는 중..
        if (is_maintenance) {
            // 점검상태
            if (is_local) {
                // 로컬이면 점검중 표기 하고 보여줘
                return <>
                    <AuthProvider>
                        <div id={'site-maintenance'}>- {t("점검중")} -</div>
                        {children}
                    </AuthProvider>
                </>
            }
            // 로컬 아니면 점검 페이지.
            return <ParsedHtmlContent html={setting.maintenance[0].content} />
        }

        // 모두 통과 하면 사이트 보여줘
        return <AuthProvider>{children}</AuthProvider>
    }


    const returnProps = {
        setting,
        SetSetting,
    }
    return (
        <SettingContext.Provider value={returnProps}>
            {loadingProcess()}
        </SettingContext.Provider>
    )
}